import React, { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { supabase } from "../services/supabaseClient";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  XCircleIcon,
  CheckCircleIcon,
  DocumentIcon,
} from "@heroicons/react/20/solid";
import DrawerSignalementDocument from "../components/DrawerSignalementDoc";

// Helpers
const first = (v, fallback = null) =>
  Array.isArray(v) ? v[0] ?? fallback : v ?? fallback;

const categoryBadgeClass = (cat) => {
  const key = (cat || "Par défaut").toString().toLowerCase();
  const map = {
    "parent 1": "bg-red-100 text-red-700",
    "parent 2": "bg-blue-100 text-blue-700",
    "personne 1": "bg-emerald-100 text-emerald-700",
    "personne 2": "bg-blue-100 text-blue-700",
    personne1: "bg-emerald-100 text-emerald-700",
    personne2: "bg-indigo-100 text-indigo-700",
    acheteur: "bg-violet-100 text-violet-700",
    vendeur: "bg-indigo-100 text-indigo-700",
    agent: "bg-indigo-100 text-indigo-700",
    commun: "bg-slate-100 text-slate-700",
    autre: "bg-red-100 text-red-700",
    "par défaut": "bg-gray-100 text-gray-700",
    "par defaut": "bg-gray-100 text-gray-700",
    "par défaut ": "bg-gray-100 text-gray-700",
  };
  return map[key] || "bg-gray-100 text-gray-700";
};

const colorsByTypeClass = {
  copropriété: "bg-rose-50 text-rose-700",
  "technique et diagnostics": "bg-sky-50 text-sky-700",
  vente: "bg-emerald-50 text-emerald-700",
  identite: "bg-indigo-50 text-indigo-700",
  identité: "bg-indigo-50 text-indigo-700",
  terrain: "bg-lime-50 text-lime-700",
  parking: "bg-purple-50 text-purple-700",
  autre: "bg-zinc-50 text-zinc-700",
  "par défaut": "bg-gray-50 text-gray-700",
  "par defaut": "bg-gray-50 text-gray-700",
};

const typeClasses = (key) =>
  colorsByTypeClass[(key || "").toString().toLowerCase()] ||
  "bg-gray-50 text-gray-700";

function Loader() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className="size-6 animate-spin"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M22.015 4.874v4.99"
      />
    </svg>
  );
}

function Erreur() {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="shrink-0">
          <XCircleIcon aria-hidden="true" className="size-5 text-red-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            Une erreur s&apos;est produite, veuillez recommencer
          </h3>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ isOpen, preview, onClose }) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[10001]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 z-[10002] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b p-4">
                  <Dialog.Title className="text-base font-semibold text-gray-900">
                    {preview.title || "Aperçu du document"}
                  </Dialog.Title>

                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded p-1 text-gray-500 hover:bg-gray-100"
                    aria-label="Fermer"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="h-[85vh] bg-gray-100">
                  {preview.url ? (
                    <iframe
                      key={preview.url}
                      title={preview.title || "Aperçu du document"}
                      src={preview.url}
                      className="h-full w-full"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-gray-500">
                      Document introuvable
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function TabsAffaireDocumentsGeneric({ affaire, type }) {
  const [documents, setDocuments] = useState(null);
  const [groupedData, setGroupedData] = useState({});
  const [groupedImmo, setGroupedImmo] = useState({});

  const [file, setFile] = useState(null);
  const [loader, setLoader] = useState(false);
  const [erreurUpload, setErreurUpload] = useState(false);
  const [isOpenUpload, setIsOpenUpload] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(null);

  const [documentData, setDocumentData] = useState({
    id_document: null,
    name_document: null,
  });

  const [isDrawerSignalementOpen, setIsDrawerSignalementOpen] = useState(false);
  const [selectedDocSignalement, setSelectedDocSignalement] = useState({
    id_doc: null,
    nom_doc: "",
  });

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [preview, setPreview] = useState({ url: null, title: "" });

  const getDocumentLinkFieldsForType = (t) => {
    const tt = (t || "").toLowerCase();

    if (tt === "immo" || tt === "immobilier") return ["id_from_transaction"];

    const map = {
      pma: ["id_from_pma"],
      mariage: ["id_from_mariage"],
      pacs: ["id_from_pacs"],
      certification: ["id_from_certification"],
      donation: ["id_from_donation"],
      divorce: ["id_from_divorce"],
      bail: ["id_from_bail"],
      succession: ["id_from_succession"],
    };

    const autres = new Set([
      "testament",
      "procuration",
      "adoption",
      "statut-societe",
    ]);

    if (autres.has(tt)) {
      return [
        "id_from_autre_demarche",
        "id_from_autre_demarches",
        "id_from_autre",
      ];
    }

    if (tt === "aucune_demarche" || tt === "aucune-demarche") {
      return ["id_from_aucune_demarche", "id_from_aucune"];
    }

    return map[tt] || ["id_from_pma"];
  };

  const groupByType = (data) =>
    data.reduce((acc, item) => {
      const cat = first(item?.fields?.categorie, "Par défaut");
      const key = String(cat || "Par défaut");
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

  const groupByRoleAndType = (data) =>
    data.reduce((acc, item) => {
      const role = first(item?.fields?.categorie, "commun");
      const typ = first(item?.fields?.type, "Autre");

      const roleKey = String(role).trim().toLowerCase();
      const typeKey = String(typ).trim();

      if (!acc[roleKey]) acc[roleKey] = {};
      if (!acc[roleKey][typeKey]) acc[roleKey][typeKey] = [];
      acc[roleKey][typeKey].push(item);
      return acc;
    }, {});

  const getUserDocuments = async () => {
    if (!affaire?.id) return;

    const fields = getDocumentLinkFieldsForType(type).filter(Boolean);
    const quotedId = String(affaire.id).replace(/'/g, "\\'");
    const formula =
      fields.length === 1
        ? `({${fields[0]}} = '${quotedId}')`
        : `OR(${fields.map((f) => `({${f}} = '${quotedId}')`).join(",")})`;

    const url = `https://api.airtable.com/v0/appD48APNaGA4GN0B/document?filterByFormula=${encodeURIComponent(
      formula
    )}`;

    try {
      const res = await fetch(url, {
        headers: {
          Authorization:
            "Bearer patfRIUbOM9xqwLV2.dfbc9a305f2124aff75634c819a8335ecd984b1d19e98f67f14013378ed6bb02",
          "content-type": "application/x-www-form-urlencoded",
        },
      });

      const data = await res.json();
      const rows = Array.isArray(data.records) ? data.records : [];

      if (type === "immobilier" || type === "immo") {
        const grouped = groupByRoleAndType(rows);
        setDocuments(rows);
        setGroupedImmo(grouped);
        setGroupedData({});
      } else {
        setDocuments(rows);
        setGroupedData(groupByType(rows));
        setGroupedImmo({});
      }
    } catch (error) {
      console.error("Erreur récupération documents :", error);
      setDocuments([]);
      setGroupedData({});
      setGroupedImmo({});
    }
  };

  useEffect(() => {
    if (affaire) getUserDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affaire, type]);

  const openDialogUpload = (id, name) => {
    setDocumentData({ id_document: id, name_document: name });
    setIsOpenUpload(true);
  };

  const closeDialogUpload = () => {
    setIsOpenUpload(false);
    setDocumentData({ id_document: null, name_document: null });
    setUploadedUrl(null);
    setFile(null);
    setErreurUpload(false);
    getUserDocuments();
  };

  const onUploadDoc = async (event) => {
    const f = event.target.files?.[0];
    if (!f) return;

    const fileSize = f.size / 1024 ** 2;
    if (fileSize >= 5) {
      alert("Votre fichier est trop volumineux. Taille maximale 5MB.");
      return;
    }

    const isPdf =
      f.type === "application/pdf" ||
      String(f.name || "").toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      alert("PDF uniquement.");
      return;
    }

    const safeName = `${Date.now()}_${f.name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9._-]/g, "_")}`;

    const filePath = `docs/${documentData.id_document}/${safeName}`;

    try {
      setLoader(true);
      setErreurUpload(false);

      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(filePath, f, {
          contentType: f.type || "application/pdf",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("documents")
        .getPublicUrl(filePath);

      setFile(f);
      setUploadedUrl(data.publicUrl);
    } catch (err) {
      console.error("Erreur upload Supabase :", err);
      setErreurUpload(true);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (file && uploadedUrl && documentData.id_document) {
      const updateDoc = async () => {
        try {
          const url = `https://api.airtable.com/v0/appD48APNaGA4GN0B/document/${documentData.id_document}`;

          await fetch(url, {
            method: "PATCH",
            headers: {
              Authorization:
                "Bearer patfRIUbOM9xqwLV2.dfbc9a305f2124aff75634c819a8335ecd984b1d19e98f67f14013378ed6bb02",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              fields: {
                etat: "ajouté",
                document: [{ url: uploadedUrl, filename: file.name }],
              },
            }),
          });

          getUserDocuments();
        } catch (error) {
          console.error("Erreur mise à jour document :", error);
        }
      };

      updateDoc();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, uploadedUrl, documentData.id_document]);

  const openDrawerSignalementDoc = (idDoc, nomDoc) => {
    setSelectedDocSignalement({ id_doc: idDoc, nom_doc: nomDoc });
    setIsDrawerSignalementOpen(true);
  };

  const handleDownload = async (url, fileName) => {
    try {
      if (!url) return;
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Erreur lors du téléchargement du fichier :", error);
    }
  };

  const openPreview = (doc) => {
    const fileData = Array.isArray(doc?.fields?.document)
      ? doc.fields.document[0]
      : null;

    const rawUrl = fileData?.url || null;
    const filename = fileData?.filename || doc?.fields?.nom || "Document";

    if (!rawUrl) {
      alert("Aucune URL de document trouvée pour cet aperçu.");
      return;
    }

    setPreview({
      url: rawUrl,
      title: filename,
    });

    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreview({ url: null, title: "" });
  };

  const getImmoDocStats = (grouped) => {
    let total = 0;
    let added = 0;

    Object.values(grouped || {}).forEach((byType) => {
      Object.values(byType || {}).forEach((docs) => {
        total += docs.length;
        added += docs.filter((d) => d.fields.etat === "ajouté").length;
      });
    });

    return { total, added };
  };

  const renderDocActions = (doc) => {
    const etat = doc?.fields?.etat;

    if (etat === "ajouté") {
      return (
        <>
          <button
            type="button"
            onClick={() => openPreview(doc)}
            className="rounded-md bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-100"
          >
            Voir
          </button>

          <button
            type="button"
            className="rounded-md bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-600 hover:bg-emerald-100 transition"
            onClick={() =>
              handleDownload(doc?.fields?.document?.[0]?.url, doc?.fields?.nom)
            }
          >
            Télécharger
          </button>

          <button
            type="button"
            className="rounded-md bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-100 transition"
            onClick={() =>
              openDrawerSignalementDoc(doc?.fields?.id_doc, doc?.fields?.nom)
            }
          >
            Signaler
          </button>
        </>
      );
    }

    if (etat === "non ajouté") {
      return (
        <button
          type="button"
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-400"
          onClick={() => openDialogUpload(doc.id, doc?.fields?.nom)}
        >
          + Ajouter
        </button>
      );
    }

    if (etat === "anomalie") {
      return (
        <>
          <div className="rounded-md border border-red-100 bg-red-50 p-4">
            <div className="flex items-start space-x-2">
              <XCircleIcon aria-hidden="true" className="h-5 w-5 text-red-400" />
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Document signalé
                </h3>
                <p className="mt-1 text-xs text-red-700">
                  Raison :{" "}
                  {doc?.fields?.message_signalement
                    ? doc.fields.message_signalement.length > 100
                      ? `${doc.fields.message_signalement.substring(0, 100)}...`
                      : doc.fields.message_signalement
                    : "Aucune raison précisée"}
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-400"
            onClick={() => openDialogUpload(doc.id, doc?.fields?.nom)}
          >
            + Ajouter
          </button>
        </>
      );
    }

    return null;
  };

  if (documents === null) {
    return (
      <div className="p-4 text-center text-gray-400">
        Chargement des documents...
      </div>
    );
  }

  if (type === "immobilier" || type === "immo") {
    const { total, added } = getImmoDocStats(groupedImmo);

    return (
      <>
        <div className="flex justify-center">
          <span className="mb-4 rounded-full bg-indigo-100 p-2 font-semibold text-indigo-800">
            {added} / {total} documents ajoutés
          </span>
        </div>

        <div className="mb-6">
          {Object.entries(groupedImmo || {}).map(([role, byType]) => (
            <div key={role} className="mb-6">
              <span className="mb-2 inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-sm font-bold text-indigo-700">
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </span>

              {Object.entries(byType).map(([typeDoc, docs]) => (
                <div
                  key={typeDoc}
                  className="mt-4 rounded-lg border border-gray-200 bg-white p-4"
                >
                  <p
                    className={`inline-flex items-center rounded-md px-2 py-1 text-sm font-bold ${typeClasses(
                      typeDoc
                    )}`}
                  >
                    {typeDoc}
                  </p>

                  <ul className="divide-y divide-gray-200">
                    {docs.map((doc) => (
                      <li
                        key={doc.id}
                        className="flex flex-col items-center justify-between py-3 sm:flex-row"
                      >
                        <div className="flex items-start space-x-3">
                          <DocumentIcon className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-base font-bold text-gray-900">
                              {doc?.fields?.nom}
                            </p>
                            <p className="text-sm text-gray-600">
                              {doc?.fields?.description}
                            </p>
                          </div>
                        </div>

                        <div className="mt-2 flex items-start space-x-2 sm:mt-0">
                          {renderDocActions(doc)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>

        <PreviewModal
          isOpen={isPreviewOpen}
          preview={preview}
          onClose={closePreview}
        />

        <Transition appear show={isOpenUpload} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[10000]"
            onClose={closeDialogUpload}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/50" />
            </Transition.Child>

            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
                  <Dialog.Title className="text-xl font-semibold text-gray-800">
                    Ajoutez votre document
                  </Dialog.Title>

                  <Dialog.Description className="mt-2 text-gray-600">
                    {documentData.name_document}
                  </Dialog.Description>

                  <div className="mt-6">
                    <input
                      onChange={onUploadDoc}
                      type="file"
                      accept="application/pdf"
                      className="block w-full text-sm file:mr-4 file:rounded file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                    />

                    {loader && <Loader />}
                    {erreurUpload && <Erreur />}

                    {file && (
                      <p className="mt-2 flex items-center text-sm text-green-600">
                        <CheckCircleIcon className="mr-1 h-5 w-5" />
                        Document prêt à envoyer
                      </p>
                    )}
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={closeDialogUpload}
                      className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                    >
                      Fermer
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>

        {isDrawerSignalementOpen && (
          <DrawerSignalementDocument
            type={type}
            id_doc={selectedDocSignalement.id_doc}
            nom_doc={selectedDocSignalement.nom_doc}
            onClose={() => setIsDrawerSignalementOpen(false)}
            onFetchSignalementSuccess={getUserDocuments}
          />
        )}
      </>
    );
  }

  const totalDocs = Array.isArray(documents) ? documents.length : 0;
  const addedDocs = Array.isArray(documents)
    ? documents.filter((d) => d.fields.etat === "ajouté").length
    : 0;

  return (
    <>
      <div className="flex justify-center">
        <span className="mb-4 rounded-full bg-indigo-100 p-2 font-semibold text-indigo-800">
          {addedDocs} / {totalDocs} documents ajoutés
        </span>
      </div>

      <div>
        {Object.entries(groupedData).map(([cat, docs]) => {
          const badgeCls = categoryBadgeClass(cat);

          return (
            <div key={cat} className="mb-6 rounded-lg bg-white p-4 shadow">
              <span
                className={`inline-flex items-center rounded-md px-2 py-1 text-sm font-bold ${badgeCls}`}
              >
                {cat}
              </span>

              <ul className="mt-4 divide-y divide-gray-200">
                {docs.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex flex-col items-center justify-between py-3 sm:flex-row"
                  >
                    <div className="flex items-start space-x-3">
                      <DocumentIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-base font-bold text-gray-900">
                          {doc?.fields?.nom}
                        </p>
                        <p className="text-sm text-gray-600">
                          {doc?.fields?.description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-2 flex items-start space-x-2 sm:mt-0">
                      {renderDocActions(doc)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <PreviewModal
        isOpen={isPreviewOpen}
        preview={preview}
        onClose={closePreview}
      />

      <Transition appear show={isOpenUpload} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[10000]"
          onClose={closeDialogUpload}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
                <Dialog.Title className="text-xl font-semibold text-gray-800">
                  Ajoutez votre document
                </Dialog.Title>

                <Dialog.Description className="mt-2 text-gray-600">
                  {documentData.name_document}
                </Dialog.Description>

                <div className="mt-6">
                  <input
                    onChange={onUploadDoc}
                    type="file"
                    accept="application/pdf"
                    className="block w-full text-sm file:mr-4 file:rounded file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                  />

                  {loader && <Loader />}
                  {erreurUpload && <Erreur />}

                  {file && (
                    <p className="mt-2 flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="mr-1 h-5 w-5" />
                      Document prêt à envoyer
                    </p>
                  )}
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={closeDialogUpload}
                    className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                  >
                    Fermer
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {isDrawerSignalementOpen && (
        <DrawerSignalementDocument
          type={type}
          id_doc={selectedDocSignalement.id_doc}
          nom_doc={selectedDocSignalement.nom_doc}
          onClose={() => setIsDrawerSignalementOpen(false)}
          onFetchSignalementSuccess={getUserDocuments}
        />
      )}
    </>
  );
}

export default TabsAffaireDocumentsGeneric;